import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Scale, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { firmInfo, navLinks, caseTypes } from '../../data/mockData';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & About */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-800 to-blue-700 rounded-lg flex items-center justify-center">
                <Scale className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  Carolina <span className="text-amber-400">&</span> Bill
                </h3>
                <p className="text-xs text-gray-400 uppercase tracking-widest">Law Firm</p>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Fighting for justice for over 40 years. We represent the injured, not big business.
            </p>
            <div className="flex gap-4">
              {[Facebook, Twitter, Linkedin, Instagram].map((Icon, index) => (
                <a
                  key={index}
                  href="#"
                  className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-blue-800 hover:text-white transition-all duration-300"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-amber-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Case Types */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Practice Areas</h4>
            <ul className="space-y-3">
              {caseTypes.slice(0, 6).map((caseType) => (
                <li key={caseType.id}>
                  <Link
                    to={`/case-types/${caseType.id}`}
                    className="text-gray-400 hover:text-amber-400 transition-colors text-sm"
                  >
                    {caseType.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${firmInfo.phoneRaw}`}
                  className="flex items-start gap-3 text-gray-400 hover:text-amber-400 transition-colors"
                >
                  <Phone className="w-5 h-5 mt-0.5 text-amber-400" />
                  <span className="text-sm">{firmInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${firmInfo.email}`}
                  className="flex items-start gap-3 text-gray-400 hover:text-amber-400 transition-colors"
                >
                  <Mail className="w-5 h-5 mt-0.5 text-amber-400" />
                  <span className="text-sm">{firmInfo.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 mt-0.5 text-amber-400 flex-shrink-0" />
                <span className="text-sm">{firmInfo.address}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {firmInfo.name}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-500 hover:text-amber-400 text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-amber-400 text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/disclaimer" className="text-gray-500 hover:text-amber-400 text-sm transition-colors">
                Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
